import { useRef, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Button, Card, SecondaryButton } from "@contractor-quoting/shared-ui";

type Stage = "choose" | "validated" | "draft" | "published";

export function PricingImportWorkspace() {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("choose");
  const [importId, setImportId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [preview, setPreview] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setMessage("");
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await apiFetch<{ importId: string; preview: unknown }>(
        "/v1/contractor/configuration/pricing_book/imports",
        { method: "POST", body: form },
      );
      setImportId(result.importId);
      setPreview(result.preview);
      setStage("validated");
      setMessage("Workbook validated. Review the summary, then save it as a draft.");
    } catch (reason) {
      setMessage(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const result = await apiFetch<{ versionId: string }>(
        `/v1/contractor/configuration/imports/${importId}/commit`,
        { method: "POST" },
      );
      setVersionId(result.versionId);
      setStage("draft");
      setMessage("Shared pricing draft saved.");
    } catch (reason) {
      setMessage(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };
  const publish = async () => {
    setBusy(true);
    try {
      await apiFetch(`/v1/contractor/configuration/pricing_book/${versionId}/publish`, {
        method: "POST",
      });
      setStage("published");
      setMessage(
        "Shared pricing published. Open the Shared pricing tab to review and activate it.",
      );
    } catch (reason) {
      setMessage(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setFile(null);
    setStage("choose");
    setImportId("");
    setVersionId("");
    setPreview(null);
    setMessage("");
    if (input.current) input.current.value = "";
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_.8fr]">
      <Card>
        <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
          First-time pricing setup
        </p>
        <h2 className="mt-2 text-2xl font-black">Upload shared pricing</h2>
        <p className="mt-3 text-stone-600">
          This is the only configuration document contractors need to provide. Runbooks are already
          available and are edited in the browser.
        </p>
        <div className="mt-6 rounded-xl border-2 border-dashed border-stone-300 p-5">
          <input
            ref={input}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setStage("choose");
              setMessage("");
            }}
          />
          <p className="mt-2 text-sm font-semibold text-stone-600">
            {file ? file.name : "No file selected"}
          </p>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {stage === "choose" ? (
            <Button disabled={!file || busy} onClick={() => void upload()}>
              {busy ? "Validating…" : "Upload and validate"}
            </Button>
          ) : null}
          {stage === "validated" ? (
            <Button disabled={busy} onClick={() => void commit()}>
              Save as pricing draft
            </Button>
          ) : null}
          {stage === "draft" ? (
            <Button disabled={busy} onClick={() => void publish()}>
              Publish shared pricing
            </Button>
          ) : null}
          {stage !== "choose" ? (
            <SecondaryButton onClick={reset}>Start over</SecondaryButton>
          ) : null}
        </div>
        {message ? (
          <p
            className={`mt-4 rounded-xl p-3 font-semibold ${message.toLowerCase().includes("invalid") || message.toLowerCase().includes("failed") ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-900"}`}
          >
            {message}
          </p>
        ) : null}
        {preview && stage === "validated" ? (
          <details className="mt-4">
            <summary className="cursor-pointer font-bold text-emerald-800">
              Validated workbook summary
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-xl bg-stone-900 p-4 text-xs text-stone-100">
              {JSON.stringify(preview, null, 2)}
            </pre>
          </details>
        ) : null}
      </Card>
      <Card>
        <h2 className="text-xl font-black">Downloads</h2>
        <p className="mt-2 text-stone-600">
          The blank workbook contains structure and example codes—not contractor prices.
        </p>
        <a
          className="mt-5 inline-flex min-h-12 items-center rounded-xl border border-emerald-800 px-5 font-bold text-emerald-900"
          href="/api/v1/contractor/configuration/pricing_book/template"
        >
          Download shared pricing template
        </a>
        <h3 className="mt-7 font-black">Small updates?</h3>
        <p className="mt-2 text-sm text-stone-600">
          Use the Shared pricing tab to change rates and settings without uploading another
          workbook.
        </p>
      </Card>
    </div>
  );
}

function errorMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : "Shared pricing import failed.";
}
